import { useState, useRef, useEffect } from "react";
import { UploadCloud, Link2, ArrowRight, Loader2, X, RefreshCw, Check, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const VIDEO_EXT = /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/i;
const MAX_VIDEO = 50 * 1024 * 1024;

function AiOrNotIcon({ size = 40 }) {
  return (
    <div className="rounded-xl border-2 border-gold bg-ink-950 flex items-center justify-center" style={{ width: size + 20, height: size + 20 }} data-testid="ai-or-not-icon">
      <div className="relative flex items-center">
        <Check size={size * 0.55} className="text-green-500" strokeWidth={3} />
        <span className="mx-[-2px] text-gold font-black" style={{ fontSize: size * 0.5 }}>/</span>
        <X size={size * 0.55} className="text-red-500" strokeWidth={3} />
      </div>
    </div>
  );
}

export default function AiOrNotModal({ open, onOpenChange }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewKind, setPreviewKind] = useState("image");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (!open) reset(); /* eslint-disable-next-line */ }, [open]);
  useEffect(() => { const h = (e) => e.key === "Escape" && onOpenChange(false); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onOpenChange]);

  const reset = () => { setFile(null); setPreview(null); setUrl(""); setResult(null); setBusy(false); setPreviewKind("image"); };

  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const isVideo = f.type.startsWith("video");
    if (isVideo && f.size > MAX_VIDEO) { toast.error("Video is too large. Please upload a clip under 50MB and 60 seconds."); e.target.value = ""; return; }
    if (isVideo) {
      const probe = document.createElement("video");
      probe.preload = "metadata";
      probe.onloadedmetadata = () => {
        window.URL.revokeObjectURL(probe.src);
        if (probe.duration > 60.5) { toast.error("This video is longer than the maximum duration currently supported for AI analysis."); return; }
        setFile(f); setUrl(""); setPreviewKind("video"); setPreview(URL.createObjectURL(f));
      };
      probe.onerror = () => { setFile(f); setUrl(""); setPreviewKind("video"); setPreview(URL.createObjectURL(f)); };
      probe.src = URL.createObjectURL(f);
      e.target.value = ""; return;
    }
    setFile(f); setUrl(""); setPreviewKind("image");
    setPreview(URL.createObjectURL(f));
    e.target.value = "";
  };

  const analyze = async () => {
    if (!file && !url.trim()) { toast.error("Upload a file or paste a link"); return; }
    setBusy(true);
    try {
      let payload;
      if (file) {
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
        payload = { media_base64: b64, kind: file.type.startsWith("video") ? "video" : "image" };
      } else {
        const link = url.trim();
        payload = { url: link, kind: VIDEO_EXT.test(link) ? "video" : "image" };
        setPreview(link); setPreviewKind(VIDEO_EXT.test(link) ? "video" : "image");
      }
      const { data } = await api.post("/ai-or-not/analyze", payload);
      setResult(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 || d?.code === "insufficient_coins") {
        toast.error("Not enough social coins. AI or Not costs 10 coins. Please refill your balance to continue.", { duration: 4000 });
      } else toast.error(formatApiError(d) || "Analysis failed");
    }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center overflow-y-auto py-6" data-testid="ai-or-not-modal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
      <div className="relative w-full max-w-md mx-3">
        <div className="bg-ink-900 border border-zinc-800 rounded-2xl p-5">
          {/* header */}
          <div className="flex items-start gap-4">
            <AiOrNotIcon size={36} />
            <div className="flex-1 pt-1">
              <h2 className="font-heading font-black text-2xl">AI or Not</h2>
              <p className="text-sm text-zinc-400 leading-snug">Check if an image or video is AI-generated</p>
            </div>
            <button onClick={() => onOpenChange(false)} data-testid="ai-or-not-close" className="text-zinc-500 hover:text-white"><X size={20} /></button>
          </div>

          {!result ? (
            <>
              <button onClick={() => fileRef.current?.click()} data-testid="ai-or-not-dropzone"
                className="mt-5 w-full rounded-2xl border-2 border-dashed border-gold/60 bg-ink-950/50 py-10 flex flex-col items-center gap-2 hover:border-gold transition-colors">
                {preview && !url ? (
                  previewKind === "video" ? <video src={preview} className="max-h-40 rounded-lg" muted /> : <img src={preview} alt="" className="max-h-40 rounded-lg object-contain" />
                ) : (
                  <>
                    <UploadCloud size={40} className="text-gold" />
                    <span className="font-heading font-bold text-lg">Drop image or video here</span>
                    <span className="text-sm text-zinc-400">or click to upload</span>
                    <span className="text-xs text-zinc-600 mt-2">Supports images and videos</span>
                  </>
                )}
              </button>
              <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onPick} className="hidden" data-testid="ai-or-not-file" />

              <div className="flex items-center gap-3 my-4"><span className="flex-1 h-px bg-zinc-800" /><span className="text-xs text-zinc-500 font-heading">OR</span><span className="flex-1 h-px bg-zinc-800" /></div>

              <div className="flex items-center gap-2 mb-2"><Link2 size={17} className="text-gold" /><span className="font-heading font-bold">Paste a link</span></div>
              <div className="flex items-center bg-ink-950 border border-zinc-800 rounded-xl px-3 focus-within:border-gold">
                <Link2 size={15} className="text-zinc-500" />
                <input value={url} onChange={(e) => { setUrl(e.target.value); setFile(null); setPreview(null); }} data-testid="ai-or-not-url"
                  placeholder="Enter image or video URL…" className="flex-1 bg-transparent px-2 py-3 text-sm focus:outline-none" />
              </div>

              <button onClick={analyze} disabled={busy} data-testid="ai-or-not-analyze"
                className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-goldlight to-gold text-ink-950 font-heading font-black text-lg py-3.5 hover:brightness-105 transition disabled:opacity-60">
                {busy ? <><Loader2 className="animate-spin" size={20} /> Analyzing…</> : <>Analyze <ArrowRight size={18} /></>}
              </button>
            </>
          ) : (
            <>
              {preview && (
                <div className="mt-4 rounded-xl overflow-hidden border border-zinc-800 bg-black flex items-center justify-center max-h-56" data-testid="ai-or-not-preview">
                  {previewKind === "video" ? <video src={preview} className="max-h-56" controls muted /> : <img src={preview} alt="" className="max-h-56 object-contain" />}
                </div>
              )}
              <div className={`mt-4 rounded-xl border p-4 ${result.is_ai ? "border-red-500/60 bg-red-500/5" : "border-green-500/50 bg-green-500/5"}`} data-testid="ai-or-not-result">
                <div className="flex items-center gap-3">
                  {result.is_ai ? <XCircle size={34} className="text-red-500 shrink-0" /> : <Check size={30} className="text-green-500 shrink-0 rounded-full border-2 border-green-500 p-0.5" />}
                  <div>
                    <div className={`font-heading font-black text-xl ${result.is_ai ? "text-red-400" : "text-green-400"}`} data-testid="ai-or-not-label">{result.label}</div>
                    <div className="text-sm text-zinc-300">AI probability: <b data-testid="ai-or-not-pct">{result.ai_probability}%</b></div>
                  </div>
                </div>
                <div className="h-px bg-white/10 my-3" />
                <p className="text-xs text-zinc-400">Disclaimer: This is a probability measure.</p>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button onClick={() => onOpenChange(false)} data-testid="ai-or-not-cancel"
                  className="flex-1 rounded-xl border border-zinc-700 text-zinc-200 font-heading font-bold py-3 hover:border-zinc-500 transition-colors">Cancel</button>
                <button onClick={reset} data-testid="ai-or-not-check-another"
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-gold text-ink-950 font-heading font-bold py-3 hover:bg-goldlight transition-colors">Check Another <RefreshCw size={16} /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
